import { Injectable } from "@angular/core";
// import { EventService } from "app/modules/events/services/event.service";
import { ConfigService } from "app/modules/shared/services/config.service";
import { RestApiService } from "app/modules/shared/services/rest-api.service";
import dayjs from "dayjs";
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as _ from 'lodash';
import { BehaviorSubject, Observable } from "rxjs";
import { AppSettings } from "../app.settings";
import { StorageService } from "./storage.service";
import { EntityService } from "./entity.service";
import { CommonBindingDataService } from "./common-binding-data.service";

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(localeData);

const TIMEZONE_ABBREVIATIONS: { [key: string]: string } = {
  'Asia/Kolkata': 'IST',
  'Australia/Sydney': 'AEST',
  'Australia/Melbourne': 'AEST',
  'Australia/Brisbane': 'AEST',
  'Australia/Hobart': 'AEST',
  'Australia/Lindeman': 'AEST',
  'Australia/Broken_Hill': 'ACST',
  'Australia/Darwin': 'ACST',
  'Australia/Adelaide': 'ACST',
  'Australia/Eucla': 'ACWST',
  'Australia/Perth': 'AWST',
  'Australia/Currie': 'AEST',
  'Australia/Lord_Howe': 'LHST',
  'Australia/Canberra': 'AEST',
  'Australia/ACT': 'AEST',
  'Australia/NSW': 'AEST',
  'Australia/North': 'ACST',
  'Australia/Queensland': 'AEST',
  'Australia/South': 'ACST',
  'Australia/Tasmania': 'AEST',
  'Australia/Victoria': 'AEST',
  'Australia/West': 'AWST',
};

function normalizeTimezone(tz: string): string {
  if (tz === 'Asia/Calcutta') return 'Asia/Kolkata';
  // Add more aliases if needed
  return tz;
}

@Injectable({
  providedIn: 'root'
})
export class MetaDataService {
  public countrySubject = new BehaviorSubject<any>(null);
  public languageSubject = new BehaviorSubject<any>(null);
  public menusSubject = new BehaviorSubject<any>(null);
  public eventResponseSubject = new BehaviorSubject<any>(null);
  public generalSettingsSubject = new BehaviorSubject<any>(null);
  public selectedTimezoneSubject = new BehaviorSubject<any>(null);
  public userSpecificTimezoneSubject = new BehaviorSubject<any>(null);
  public returnReasonVisibilitySubject = new BehaviorSubject<boolean>(false);
  country$ = this.countrySubject.asObservable();
  language$ = this.languageSubject.asObservable();
  menus$ = this.menusSubject.asObservable();
  eventResponse$ = this.eventResponseSubject.asObservable();
  generalSettings$ = this.generalSettingsSubject.asObservable();
  selectedTimezone$ = this.selectedTimezoneSubject.asObservable();
  userSpecificTimezone$ = this.userSpecificTimezoneSubject.asObservable();
  returnReasonVisibility$ = this.returnReasonVisibilitySubject.asObservable();
  public mobileNumber: string = '';
  public currency: string = '';
  public timeZone: string = '';
  public timeFormat: string = '';
  public timeHourFormat: string = '';
  public dateTimeFormat: string = '';
  public dateTimeFormatPipe: string = '';
  public timeDateFormat: string = '';
  public systemOfMeasurement: string = '';
  timeSettingsEntityId: any;
  eventId: any;
  public attributeValueOfBookingMinTime;
  public lowerDateFormat: string = '';
  public upperDateFormat: string = '';
  public upperDateTimeFormat: string = '';
  public upperDateTimeFormatPipe: string = '';
  public upperTimeDateFormat: string = '';
  public upperAllDateFormat: string = '';
  public upperAllDateTimeFormat: string = '';
  public upperAllDateTimeFormatPipe: string = '';
  public upperAllTimeDateFormat: string = '';
  public selectedTimeZone: string = '';
  public userSpecificTimeZone: string = '';
  public minDateForBooking;
  public maxDateForBooking;
  public latitude: number | null = null;
  public longitude: number | null = null;
  constructor(private restApiService: RestApiService, private confService: ConfigService,
    // private eventService: EventService, 
    private storageService: StorageService,
    private entityService: EntityService, private cs: CommonBindingDataService) { }

  updateMetaData(country: any, language: any, menus: any, eventResponse?: any, generalSettings?: any) {
    this.updateSubjects(country, language, menus, eventResponse, generalSettings);
    if (eventResponse) {
      this.initializeEventData(eventResponse);
    }
    this.initializeTimezone();
  }

  getCurrencies() {
    return this.restApiService.get('GET CURRENCIES', `${this.confService.getLoggedInTenantCode()}/api/v1/currencies?forTenantCode=${this.confService.getForTenantCode()}`, 'page-center');
  }

  private updateSubjects(country: any, language: any, menus: any, eventResponse?: any, generalSettings?: any) {
    this.countrySubject.next(country);
    this.languageSubject.next(language);
    this.menusSubject.next(menus);
    if (eventResponse) {
      this.eventResponseSubject.next(eventResponse);
    }
    if (generalSettings) {
      this.generalSettingsSubject.next(generalSettings);
    }
  }

  private initializeEventData(eventResponse: any) {
    const eventCode = JSON.parse(localStorage.getItem('eventCodeData'));
    // this.getTimeSettings(eventCode);
    const event = _.find(eventResponse, { eventShortcode: eventCode }) || eventResponse[0];
    this.eventId = event?.eventId;
    // this.eventService.getEvent(this.eventId).subscribe((res: any) => {
    //   const tenantId = res?.tenantId;
    //   this.storageService.setItem(AppSettings.TENANT_ID, tenantId);
    //   this.updateEventFormats(res);
    //   this.updateEventDates(res);
    //   this.initializeGeneralSettings();
    // });
  }

  private updateEventFormats(eventData: any) {
    if (eventData.shortDateFormat) {
      this.lowerDateFormat = this.formatDateString(eventData.shortDateFormat);
      this.upperDateFormat = this.reverseFormatDateString(eventData.shortDateFormat);
      this.upperAllDateFormat = this.reverseAllFormatDateString(eventData.shortDateFormat)
    }
    if (eventData.timeFormat) {
      this.timeFormat = this.getTimeFormatFromEvent(eventData.timeFormat);
    }
    if (eventData.latitude && eventData.longitude) {
      this.latitude = eventData.latitude;
      this.longitude = eventData.longitude;
    }
    this.setDateTimeFormats();
  }

  private updateEventDates(eventData: any) {
    this.minDateForBooking = eventData.eventStartDateTime ? new Date(eventData.eventStartDateTime) : new Date();
    this.maxDateForBooking = eventData.eventEndDateTime ? new Date(eventData.eventEndDateTime) : new Date();
  }

  private initializeGeneralSettings() {
    this.generalSettings$.subscribe((settings) => {
      if (settings) {
        this.setBasicSettings(settings);
        this.setTimeFormat(settings);
        this.setDateTimeFormats();
      }
    });
  }

  private initializeTimezone() {
    this.selectedTimezone$.subscribe((res) => {
      if (!res) {
        return;
      }
      this.selectedTimeZone = res;
      localStorage.setItem(AppSettings.CURRENT_TIME_ZONE, this.selectedTimeZone);
    });

    this.userSpecificTimezone$.subscribe((res) => {
      if (!res) {
        return;
      }
      this.userSpecificTimeZone = res;
      localStorage.setItem(AppSettings.USER_TIME_ZONE, this.userSpecificTimeZone);
    });
  }

  private setBasicSettings(settings: any) {
    this.mobileNumber = this.getMobileNumber(settings);
    this.currency = this.getCurrency(settings);
    this.timeZone = this.getTimeZone(settings);
    this.systemOfMeasurement = this.getSystemOfMeasurement(settings);
    const shortDateFormat = settings.short_date_format || AppSettings.dd_MMM_yyyy_FORMAT;
    if (!this.lowerDateFormat) {
      this.lowerDateFormat = this.formatDateString(shortDateFormat);
      this.upperDateFormat = this.reverseFormatDateString(shortDateFormat);
      this.upperAllDateFormat = this.reverseAllFormatDateString(shortDateFormat);
    }
    this.lowerDateFormat = this.lowerDateFormat || shortDateFormat;
    this.upperDateFormat = this.upperDateFormat || this.reverseFormatDateString(this.lowerDateFormat);
    this.upperAllDateFormat = this.upperAllDateFormat || this.reverseAllFormatDateString(this.lowerDateFormat);
  }


  private setTimeFormat(settings: any) {
    if (!this.timeFormat) {
      this.timeFormat = this.getTimeFormat(settings);
    }
    this.timeHourFormat = this.getTimeHourFormat(this.timeFormat);
  }

  public getTimeFormatFromEvent(timeFormat: string): string {
    if (timeFormat === '24- hours format') {
      return '24';
    } else if (timeFormat === '12- hours format') {
      return '12';
    }
    return '12';
  }

  private setDateTimeFormats() {
    this.dateTimeFormat = this.getDateTimeFormat(this.lowerDateFormat, this.timeHourFormat);
    this.dateTimeFormatPipe = this.getDateTimeFormatPipe(this.lowerDateFormat, this.timeHourFormat);
    this.timeDateFormat = this.getTimeDateFormat(this.timeHourFormat, this.lowerDateFormat);
    this.upperDateTimeFormat = this.getDateTimeFormat(this.upperDateFormat, this.timeHourFormat);
    this.upperDateTimeFormatPipe = this.getDateTimeFormatPipe(this.upperDateFormat, this.timeHourFormat);
    this.upperTimeDateFormat = this.getTimeDateFormat(this.timeHourFormat, this.upperDateFormat);
    this.upperAllDateTimeFormat = this.getDateTimeFormat(this.upperAllDateFormat, this.timeHourFormat);
    this.upperAllDateTimeFormatPipe = this.getDateTimeFormatPipe(this.upperAllDateFormat, this.timeHourFormat);
    this.upperAllTimeDateFormat = this.getTimeDateFormat(this.timeHourFormat, this.upperAllDateFormat);
  }

  private getMobileNumber(settings: any): string {
    return settings.mobile_number || AppSettings.DEFAULT_MOBILE_NUMBER_FORMAT;
  }

  private getCurrency(settings: any): string {
    return settings.currency || '';
  }

  private getTimeZone(settings: any): string {
    return settings.time_zone || '';
  }

  private getSystemOfMeasurement(settings: any): string {
    return settings.system_of_measurement || AppSettings.SET_UNIT;
  }

  private getTimeFormat(settings: any): string {
    const timeFormat = settings.time_format || AppSettings.HOUR_FORMAT;
    return timeFormat === '24- hours format' ? '24' : '12';
  }

  private getTimeHourFormat(timeFormat: string): string {
    return timeFormat === '24'
      ? AppSettings.HOUR_MINUTE
      : AppSettings.HOUR_MINUTE_FORMAT;
  }

  private getDateTimeFormat(dateFormat: string, timeHourFormat: string): string {
    return `${dateFormat} ${timeHourFormat}`;
  }

  private getDateTimeFormatPipe(dateFormat: string, timeHourFormat: string): string {
    return `${dateFormat} | ${timeHourFormat}`;
  }

  private getTimeDateFormat(timeHourFormat: string, dateFormat: string): string {
    return `${timeHourFormat} ${dateFormat}`;
  }

  metaDataMultipleApi(data): Observable<any> {
    return this.restApiService.post('METADATA MULTIPLE API CALL', `${this.confService.getLoggedInTenantCode()}/api/v1/multi-api-call`, data, '');
  }


  appConfig(entityCode, entityData): Observable<any> {
    if (this.confService.getLoggedInTenantCode()) {
      const url = `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/search`;
      return this.restApiService.post('SEARCH ENTITY', url, entityData, 'page-center');
    }
  }


  getDate(date) {
    return dayjs(date).format(this.lowerDateFormat);
  }

  getUpperDate(date) {
    return dayjs(date).format(this.upperDateFormat);
  }

  formatDateString(dateFormat: string): string {
    dateFormat = dateFormat.replace(/MM/g, 'mm');
    dateFormat = dateFormat.replace(/yyyy/g, 'yy');
    return dateFormat;
  }

  reverseFormatDateString(dateFormat: string): string {
    dateFormat = dateFormat.replace(/mm/g, 'MM');
    if (!dateFormat.includes('yyyy')) {
      dateFormat = dateFormat.replace(/yy/g, 'yyyy');
    }
    return dateFormat;
  }

  reverseAllFormatDateString(dateFormat: string): string {
    dateFormat = dateFormat.replace(/mm/g, 'MM');
    dateFormat = dateFormat.replace(/yy/g, 'YYYY');
    dateFormat = dateFormat.replace(/dd/g, 'DD');
    return dateFormat;
  }

  getSystemTimezone() {
    const systemTimezone = dayjs.tz.guess();
    return systemTimezone;
  }

  getSelectedTimezone(): string {
    const currentTimeZone = localStorage.getItem(AppSettings.USER_TIME_ZONE);
    this.userSpecificTimeZone = this.userSpecificTimeZone ? this.userSpecificTimeZone : currentTimeZone;
    if (this.userSpecificTimeZone) {
      return this.userSpecificTimeZone;
    } else {
      return this.timeZone || this.getSystemTimezone();
    }
  }

  getCurrentTimeInTimezone() {
    const now = dayjs().tz(this.getSelectedTimezone());
    return new Date(now.format('YYYY-MM-DD H:mm'));
  }

  getCurrentDateInTimezone() {
    const now = dayjs().tz(this.getSelectedTimezone());
    return new Date(now.format('YYYY-MM-DD'));
  }

  formatDateInTimezone(date: Date): string {
    const selectedTimezone = this.getSelectedTimezone();
    return dayjs.utc(date).tz(selectedTimezone).format('YYYY-MM-DD H:mm');
  }

  formatDateInTZ(date: Date): string {
    const selectedTimezone = this.getSelectedTimezone();
    return dayjs.utc(date).tz(selectedTimezone).format('MMM DD, YYYY');
  }

  formatDateTimeOnTZ(date: any, format: string): string {
    let selectedTimezone: string;
    selectedTimezone = this.getSelectedTimezone();
    if (!selectedTimezone) {
      selectedTimezone = this.getSystemTimezone();
    }
    selectedTimezone = normalizeTimezone(selectedTimezone);
    const d = dayjs.tz(date, selectedTimezone);
    let abbreviation = TIMEZONE_ABBREVIATIONS[selectedTimezone] || d.format('z');
    if (!abbreviation || abbreviation === 'z' || abbreviation.match(/^[+-]/)) {
      abbreviation = d.format('Z'); // fallback to offset
    }
    if (format.includes(':mm')) {
      return `${d.format(format)} (${abbreviation})`;
    } else {
      return d.format(format);
    }
  }

  // getTimeSettings(eventCode) {
  //   this.eventService.getEventSettingsDetailsByView(AppSettings.ENTITY_TYPE.TIME_SETTINGS, eventCode, {})
  //     .subscribe({
  //       next: (response: any) => {
  //         if (response) {
  //           this.timeSettingsEntityId = response.entityId;
  //           this.getEntityDetails(eventCode);
  //         }
  //       },
  //       error: () => {
  //         this.getTimeSettingsFromMainSettings();
  //       }
  //     })
  // }

  // getTimeSettingsFromMainSettings() {
  //   this.entityService.getEntityDetailsByView(AppSettings.ENTITY_TYPE.TIME_SETTINGS, {}).subscribe({
  //     next: (response: any) => {
  //       this.getEntityDetailsFromMainSettings(response.entityId);
  //     },
  //     error: () => {}
  //   })
  // }

  // getEntityDetailsFromMainSettings(entityId) {
  //   this.entityService.getEntity(entityId, AppSettings.ENTITY_TYPE.TIME_SETTINGS, AppSettings.VIEW_CODE.ADD_EDIT_VIEW).subscribe({
  //     next: (response: any) => {
  //       const attribute = response.attributeCodeValueDtoList.find(attr => attr.attributeCode === "min_valid_time_for_booking_portal_user");
  //       if (attribute) {
  //         this.attributeValueOfBookingMinTime = attribute.attributeValue;
  //       }
  //     },
  //     error: () => {}
  //   })
  // }

  // getEntityDetails(eventCode) {
  //   this.eventService.getEventSettingsEntity(this.timeSettingsEntityId, AppSettings.ENTITY_TYPE.TIME_SETTINGS, AppSettings.VIEW_CODE.ADD_EDIT_VIEW, eventCode).subscribe({
  //     next: (response: any) => {
  //       const attribute = response.attributeCodeValueDtoList.find(attr => attr.attributeCode === "min_valid_time_for_booking_portal_user");
  //       if (attribute) {
  //         this.attributeValueOfBookingMinTime = attribute.attributeValue;
  //       }
  //     },
  //     error: () => {}
  //   })
  // }

  getTimeZoneFormattedDate(date: Date): any {
    let selectedTimezone = localStorage.getItem(AppSettings.CURRENT_TIME_ZONE);
    if (!selectedTimezone) {
      selectedTimezone = this.getSystemTimezone();
    }
    return dayjs.tz(date, selectedTimezone).startOf('day').toDate();
  }

  setDateInTimezone(date: number): number {
    let selectedTimezone = localStorage.getItem(AppSettings.USER_TIME_ZONE);
    if (!selectedTimezone) {
      selectedTimezone = this.getSystemTimezone();
    }
    const localOffset = dayjs(date).utcOffset();
    const selectedOffset = dayjs(date).tz(selectedTimezone).utcOffset();
    const offsetDiffMinutes = selectedOffset - localOffset;
    const offsetDiffMs = offsetDiffMinutes * 60 * 1000;
    return date + offsetDiffMs;
  }

  setDateFilterOffset(date: number): number {
    let selectedTimezone = localStorage.getItem(AppSettings.USER_TIME_ZONE);
    if (!selectedTimezone) {
      selectedTimezone = this.getSystemTimezone();
    }
    const localOffset = dayjs(date).utcOffset();
    const selectedOffset = dayjs(date).tz(selectedTimezone).utcOffset();
    const offsetDiffMinutes = localOffset - selectedOffset;
    const offsetDiffMs = offsetDiffMinutes * 60 * 1000;
    return date + offsetDiffMs;
  }

  getTimezoneAbbreviation(timezone: string): string {
    const normalizedTimezone = normalizeTimezone(timezone);
    return TIMEZONE_ABBREVIATIONS[normalizedTimezone] || timezone.split('/').pop()?.toUpperCase() || timezone;
  }
}
